import type { components } from '../../api/schema';

export type Rect = components['schemas']['GridRect'];
export type Slot = components['schemas']['Slot'];
export type Board = components['schemas']['Board'];

/** Celulele (x,y) acoperite de un dreptunghi din grilă. */
export function cellsOf(r: Rect): string[] {
  const out: string[] = [];
  for (let y = r.y; y < r.y + r.h; y++) for (let x = r.x; x < r.x + r.w; x++) out.push(`${x},${y}`);
  return out;
}

export function collides(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

/** Celulele ocupate de ecran și de compartimente (opțional fără unul dintre ele). */
export function occupiedCells(
  slots: Pick<Slot, 'id' | 'rect'>[],
  control: Rect,
  exceptSlotId?: string,
): Set<string> {
  const set = new Set(cellsOf(control));
  for (const s of slots) if (s.id !== exceptSlotId) for (const c of cellsOf(s.rect)) set.add(c);
  return set;
}

/** Prima celulă liberă (stânga → dreapta, sus → jos) unde încape un dreptunghi w×h. */
export function firstFreeCell(
  cols: number,
  occupied: Set<string>,
  w = 1,
  h = 1,
  maxRows = 200,
): Rect | null {
  for (let y = 0; y < maxRows; y++) {
    for (let x = 0; x + w <= cols; x++) {
      const r = { x, y, w, h };
      if (cellsOf(r).every((c) => !occupied.has(c))) return r;
    }
  }
  return null;
}

/** Numărul de rânduri necesare grilei (minimum 4, ca să nu pară goală). */
export function rowsOf(slots: Pick<Slot, 'rect'>[], control: Rect): number {
  const bottoms = [control.y + control.h, ...slots.map((s) => s.rect.y + s.rect.h)];
  return Math.max(4, ...bottoms);
}

/** Mutarea cu săgeți, limitată la grilă (nu verifică suprapunerile: le raportează validarea serverului). */
export function moveRect(r: Rect, dx: number, dy: number, cols: number): Rect {
  const x = Math.max(0, Math.min(cols - r.w, r.x + dx));
  const y = Math.max(0, r.y + dy);
  return { ...r, x, y };
}

export function resizeRect(r: Rect, dw: number, dh: number, cols: number): Rect {
  const w = Math.max(1, Math.min(cols - r.x, r.w + dw));
  const h = Math.max(1, Math.min(8, r.h + dh));
  return { ...r, w, h };
}

/** Ieșirile (placă, ușă) nefolosite încă. */
export function freeOutputs(
  boards: Board[],
  slots: Pick<Slot, 'id' | 'boardId' | 'door'>[],
  exceptSlotId?: string,
): { boardId: string; door: number }[] {
  const used = new Set(
    slots.filter((s) => s.id !== exceptSlotId).map((s) => `${s.boardId}:${s.door}`),
  );
  const out: { boardId: string; door: number }[] = [];
  for (const b of boards)
    for (let d = 1; d <= b.doors; d++)
      if (!used.has(`${b.id}:${d}`)) out.push({ boardId: b.id, door: d });
  return out;
}

/** Eticheta următoare: numerică cu două cifre dacă toate cele existente sunt numerice, altfel prima liberă din A…. */
export function nextLabel(slots: Pick<Slot, 'label'>[]): string {
  const labels = new Set(slots.map((s) => s.label));
  if (slots.every((s) => /^\d+$/.test(s.label))) {
    let n = slots.length + 1;
    while (labels.has(String(n).padStart(2, '0'))) n++;
    return String(n).padStart(2, '0');
  }
  for (let i = 1; i < 1000; i++) {
    const l = `C${i}`;
    if (!labels.has(l)) return l;
  }
  return `C${Date.now() % 1000}`;
}
